/* eslint-disable prettier/prettier */
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/shared/Logo';
import { NavItem } from '../components/ui/NavItem';
import { Files, LogOut, Menu, UserPen, KeyRound } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { NAV_ITEMS } from '../constants/navItems';
import { EditProfileModal } from '../components/profile/EditProfileModal';
import { ChangePasswordModal } from '../components/profile/ChangePasswordModal';
import { CompanyModal } from '../components/company/CompanyModal';
import { CompanyType } from '../types/CompanyType';
import { useDebounce } from 'use-debounce';



export const DashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<CompanyType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [previousPath, setPreviousPath] = useState<string>('/');

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { logout, user } = useAuth();
  const role = user?.role?.name as keyof typeof NAV_ITEMS;
  const navItems = NAV_ITEMS[role] || [];

  const location = useLocation();
  const navigate = useNavigate();
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    if (!location.pathname.startsWith('/search')) {
      setSearchTerm('');
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();

    if (trimmed !== '') {
      if (!location.pathname.startsWith('/search')) {
        setPreviousPath(location.pathname + location.search);
      }
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    } else if (location.pathname.startsWith('/search')) {
      navigate(previousPath);
    }
  }, [debouncedSearchTerm, navigate, location.pathname, previousPath]);

  const handleNav = (path: string) => {
    setSearchTerm('');
    navigate(path);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        className={`fixed z-40 inset-y-0 left-0 w-64 bg-muted text-primary transition-transform transform 
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} sm:translate-x-0`}
      >
        <div className="py-4 px-7">
          <Logo size="lg" onClick={true} />
        </div>
        <div className="w-full border-b-2 border-primary" />
        <nav className="mt-4 space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
          ))}
        </nav>
        <div className="absolute w-full bottom-1 sm:hidden">
          {user?.role?.name === 'COMPANY_ADMIN' && (
            <NavItem
              label="Company Info"
              onClick={() => {
                setCurrentCompany(
                  user?.company
                    ? {
                        id: user.company.id,
                        name: user.company.name,
                        country: user.company.country,
                        isActive: user.company.isActive,
                        logo: '',
                      }
                    : null,
                );
                setIsEditCompanyOpen(true);
              }}
              icon={<Files />}
            />
          )}
          <NavItem
            label="Edit Profile"
            icon={<UserPen />}
            onClick={() => setIsEditProfileOpen(true)}
          />
          <NavItem
            label="Change Password"
            icon={<KeyRound />}
            onClick={() => setIsChangePasswordOpen(true)}
          />
          <NavItem label="Logout" icon={<LogOut />} onClick={logout} />
        </div>
      </aside>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-25 sm:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="flex flex-col flex-1 pl-0 sm:pl-64">
        <header className="fixed top-0 right-0 left-0 sm:left-64 z-30 h-[70px] flex items-center justify-between px-7 border-b-2 border-primary bg-white">
          <button className="sm:hidden" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <Menu className="w-7 h-7 text-primary" />
          </button>

          <div className="flex justify-center flex-1 ml-1 sm:justify-start">
            <div className="relative w-full max-w-[500px]">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-4 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="absolute left-2 top-2.5 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M16 10a6 6 0 11-12 0 6 6 0 0112 0z"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div className="relative mt-2">
            <button
              ref={buttonRef}
              className="text-primary"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <FaUserCircle className="w-7 h-7" />
            </button>
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="absolute right-0 z-50 mt-2 bg-white border rounded-lg shadow w-44"
              >
                {user?.role?.name === 'COMPANY_ADMIN' && (
                  <button
                    className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-primary hover:bg-muted"
                    onClick={() => {
                      setCurrentCompany(
                        user?.company
                          ? {
                              id: user.company.id,
                              name: user.company.name,
                              country: user.company.country,
                              isActive: user.company.isActive,
                              logo: '',
                            }
                          : null,
                      );
                      setIsEditCompanyOpen(true);
                    }}
                  >
                    <Files className="w-4 h-4" />
                    <span>Company Info</span>
                  </button>
                )}
                <button
                  className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-primary hover:bg-muted"
                  onClick={() => {
                    setShowDropdown(false);
                    setIsEditProfileOpen(true);
                  }}
                >
                  <UserPen className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
                <button
                  className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-primary hover:bg-muted"
                  onClick={() => {
                    setShowDropdown(false);
                    setIsChangePasswordOpen(true);
                  }}
                >
                  <KeyRound className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
                <button
                  className="flex items-center w-full gap-2 px-4 py-2 text-sm text-left text-primary hover:bg-muted"
                  onClick={logout}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="mt-[70px] p-8 overflow-y-auto flex-1">
          <Outlet />
        </main>
      </div>

      <CompanyModal
        isOpen={isEditCompanyOpen}
        onClose={() => setIsEditCompanyOpen(false)}
        onSuccess={() => {}}
        editingCompany={currentCompany}
      />
      <EditProfileModal isOpen={isEditProfileOpen} onClose={() => setIsEditProfileOpen(false)} />
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </div>
  );
};
